// 共通のコンソールページ枠組み
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, TextField, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton, FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteIcon from "@mui/icons-material/Delete";
import ConsoleTable from "./ConsoleTable";
import useConsoleData from "../Data/useConsoleData";
import { useToast } from "./ToastProvider";
import { useStatusDialog } from "../../../../common/StatusDialogProvider";
import supabase from "../../../../../utils/supabase";

/**
 * 汎用コンソールページ
 *
 * props:
 * - title: 見出し
 * - plugin: useConsoleData 用プラグイン
 * - columnsBuilder: ({ onEdit }) => columns
 * - renderAddDialog: ({ onClose }) => ReactNode
 * - renderEditDialog: ({ row, onClose }) => ReactNode
 * - searchPlaceholder: 検索欄プレースホルダ
 * - addButtonLabel: 追加ボタンのラベル
 * - initialPageSize: 初期ページサイズ
 * - getRowId: ConsoleTable に渡す rowId 取得関数
 * - searchFieldProps: 検索欄に追加したい props (sx は上書きマージ)
 * - headerExtras: 追加のヘッダー要素（関数 or ReactNode）
 * - toastMessages: { create?, update? } 保存成功時のトースト
 * - onRowClick: 行クリックのハンドラを差し替えたい場合
 */
export default function ConsolePageFrame({
  title,
  plugin,
  columnsBuilder,
  renderAddDialog,
  renderEditDialog,
  searchPlaceholder = "Search",
 addButtonLabel = "Add",
  initialPageSize = 25,
  getRowId,
  searchFieldProps = {},
 headerExtras = null,
  toastMessages = {},
  onRowClick,
  addButtonProps = {},
  enableDelete = true,
  inlineBanner = null, // optional: content to render above the table (e.g., active filters)
  externalRefreshKey = 0, // force reload from parent
  onDeleteError = null, // optional: (error, ids) => handled:boolean
}) {
  const defaultSort = plugin?.defaultSort || { field: "id", sort: "asc" };

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortModel, setSortModel] = useState([
    { field: defaultSort.field, sort: defaultSort.sort },
  ]);
  const [searchInput, setSearchInput] = useState("");
  const [searchCommitted, setSearchCommitted] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchOptionsOpen, setSearchOptionsOpen] = useState(false);
  const [combineMode, setCombineMode] = useState("AND");
  const [matchMode, setMatchMode] = useState("partial");
  const [fuzzyEnabled, setFuzzyEnabled] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const toast = useToast();
  const { showStatus } = useStatusDialog();

  const { rows, total, loading, error } = useConsoleData({
    plugin,
    page,
    pageSize,
    sortModel,
    search: searchCommitted,
    searchOptions: { combineMode, matchMode, fuzzyEnabled },
    refreshKey: refreshKey + externalRefreshKey,
  });

  const columns = useMemo(
    () =>
      columnsBuilder
        ? columnsBuilder({ onEdit: setEditRow })
        : plugin?.columns?.({ onEdit: setEditRow }) || [],
    [columnsBuilder, plugin]
  );
  const rowMap = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  // プラグインの切り替え時（親＋子のみ表示など）はページ位置をリセットする
  useEffect(() => {
    setPage(0);
  }, [plugin]);

  const handleSaved = (type, saved) => {
    if (!saved) return;
    setRefreshKey((k) => k + 1);
    const message = type === "add" ? toastMessages.create : toastMessages.update;
    if (message && toast) {
      toast.push("success", message);
    }
  };

  const effectiveRowClick =
    onRowClick || (renderEditDialog ? (row) => setEditRow(row) : undefined);

  const { sx: searchSx, ...restSearchProps } = searchFieldProps;
  const headerExtraContent =
    typeof headerExtras === "function"
      ? headerExtras({ openAdd: () => setAddOpen(true) })
      : headerExtras;

  const hasChildSelected = selectionModel.some((id) => (rowMap.get(id)?._childCount || 0) > 0);
  const deleteDisabled = !selectionModel.length || hasChildSelected;

  useEffect(() => {
    if (error) {
      showStatus({
        severity: "error",
        title: `${title} data error`,
        description: error.message || "Failed to load data. Please retry in a moment.",
      });
    }
  }, [error, showStatus, title]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              setPage(0);
              setSearchCommitted(e.target.value);
              setRefreshKey((k) => k + 1); // 再検索トリガー
            }
          }}
          sx={{ minWidth: 280, ...searchSx }}
          {...restSearchProps}
        />
        <Tooltip title="Search">
          <IconButton
            color="primary"
            onClick={() => {
              setPage(0);
              setSearchCommitted(searchInput);
              setRefreshKey((k) => k + 1);
            }}
            sx={{
              bgcolor: (theme) => theme.palette.primary.main,
              color: (theme) => theme.palette.primary.contrastText,
              "&:hover": { bgcolor: (theme) => theme.palette.primary.dark },
            }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Search options">
          <IconButton color="primary" onClick={() => setSearchOptionsOpen(true)}>
            <TuneIcon />
          </IconButton>
        </Tooltip>
        {headerExtraContent}
        {enableDelete && (
          <Tooltip title={selectionModel.length ? "Delete selected" : "Select rows to delete"}>
            <span>
              <IconButton
                color="error"
                disabled={!selectionModel.length}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {renderAddDialog && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            {...addButtonProps}
          >
            {addButtonLabel}
          </Button>
        )}
      </Stack>

      {inlineBanner}

      <ConsoleTable
        columns={columns}
        rows={rows}
        total={total}
        page={page}
        onPageChange={(p) => setPage(p)}
        pageSize={pageSize}
        onPageSizeChange={(s) => {
          setPage(0);
          setPageSize(s);
        }}
        sortModel={sortModel}
        onSortModelChange={(m) => {
          setPage(0);
          setSortModel(m);
        }}
        onRowClick={effectiveRowClick}
        loading={loading}
        getRowId={getRowId}
        selectionModel={enableDelete ? selectionModel : []}
        onSelectionModelChange={(m) => enableDelete && setSelectionModel(m)}
        checkboxSelection={enableDelete}
      />

      {addOpen &&
        renderAddDialog?.({
          onClose: (saved) => {
            setAddOpen(false);
            handleSaved("add", saved);
          },
        })}

      {editRow &&
        renderEditDialog?.({
          row: editRow,
          onClose: (saved) => {
            setEditRow(null);
            handleSaved("edit", saved);
          },
        })}

      {enableDelete && (
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete selected records?</DialogTitle>
          <DialogContent dividers>
              {selectionModel.length === 0 ? (
                <Typography>No rows selected.</Typography>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    The following {selectionModel.length} record(s) will be deleted:
                  </Typography>
                  <Box sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Label</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectionModel.map((id) => {
                          const row = rowMap.get(id) || {};
                          const label = row?.name || row?.username || row?.title || row?.id || id;
                          return (
                            <TableRow key={id}>
                              <TableCell>{id}</TableCell>
                              <TableCell>{label}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                  {hasChildSelected && (
                    <Typography color="error">
                      Some selected records have children. Please clear child relationships first.
                    </Typography>
                  )}
                </Stack>
              )}
            </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteDisabled}
              onClick={async () => {
                try {
                  const ids = selectionModel;
                  const { error: delErr } = await supabase.from(plugin.table || "scientific_names").delete().in("id", ids);
                  if (delErr) throw delErr;
                  setDeleteConfirmOpen(false);
                  setSelectionModel([]);
                  setRefreshKey((k) => k + 1);
                  toast?.push("success", "Deleted");
                } catch (e) {
                  const handled = typeof onDeleteError === "function" ? onDeleteError(e, selectionModel) : false;
                  if (!handled) {
                    showStatus({
                      severity: "error",
                      title: "Delete failed",
                      description: e.message || "Failed to delete records",
                    });
                  } else {
                    setDeleteConfirmOpen(false);
                  }
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={searchOptionsOpen} onClose={() => setSearchOptionsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Search Options</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ minWidth: 88 }}>Combine</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={combineMode}
                onChange={(_, v) => v && setCombineMode(v)}
              >
                <ToggleButton value="AND">AND</ToggleButton>
                <ToggleButton value="OR">OR</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ minWidth: 88 }}>Match</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={matchMode}
                onChange={(_, v) => v && setMatchMode(v)}
              >
                <ToggleButton value="partial">Partial</ToggleButton>
                <ToggleButton value="exact">Exact</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 88 }}>Fuzzy</Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={fuzzyEnabled}
                    onChange={(e) => setFuzzyEnabled(e.target.checked)}
                  />
                }
                label="Allow spelling variance"
                sx={{ ml: 0 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchOptionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
